import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import type { Resolver } from 'react-hook-form'
import type { JSONContent } from '@tiptap/core'
import { ArticleFormValues } from 'Models'
import { tiptapToBackend, validateBlocks } from 'Adapters/TipTapAdapter'

/**
 * Two validation modes over the same form:
 *
 *  - draft:   only what the backend needs to store an incomplete draft (a
 *             title) plus its length/format limits. Used by autosave and Save Draft.
 *  - publish: everything needed to go live. Keeps the product rules already in
 *             the existing UI (excerpt, category, tag, cover are marked required)
 *             on top of the backend's own publish rules (non-empty content,
 *             alt text for a cover image).
 *
 * Autosave never runs these schemas: an incomplete draft must be saveable.
 * Only the Save Draft and Publish buttons trigger validation.
 */

export type BlogFormValues = ArticleFormValues & { visibility?: string }

const HTTP_URL = /^https?:\/\/[^\s/$.?#].[^\s]*$/i

const optionalUrl = (label: string) =>
  yup
    .string()
    .trim()
    .matches(HTTP_URL, {
      message: `${label} must be a valid http(s) URL`,
      excludeEmptyString: true,
    })
    .optional()

const blocksOf = (value?: JSONContent | null) => (value ? tiptapToBackend(value) : null)

/** Content rules shared by both modes: supported nodes only, and within backend size limits. */
const contentIsConvertible = (value?: JSONContent | null): true | yup.ValidationError => {
  const converted = blocksOf(value)
  if (!converted) return true
  if (converted.unsupportedNodeTypes.length > 0) {
    return new yup.ValidationError(
      `Unsupported content: ${converted.unsupportedNodeTypes.join(', ')}. Remove it to save.`,
      value,
      'content',
    )
  }
  const issues = validateBlocks(converted.blocks)
  return issues.length > 0 ? new yup.ValidationError(issues[0], value, 'content') : true
}

const baseFields = {
  title: yup
    .string()
    .trim()
    .required('Title is required')
    .max(200, 'Title must be 200 characters or fewer'),
  excerpt: yup.string().trim().max(300, 'Excerpt must be 300 characters or fewer').optional(),
  category: yup.string().trim().max(80, 'Category must be 80 characters or fewer').optional(),
  tags: yup
    .array()
    .of(yup.string().trim().max(50, 'Each tag must be 50 characters or fewer').required())
    .max(20, 'Use at most 20 tags')
    .optional(),
  coverImageAltText: yup
    .string()
    .trim()
    .max(300, 'Alt text must be 300 characters or fewer')
    .optional(),
  metaTitle: yup.string().trim().max(70, 'SEO title must be 70 characters or fewer').optional(),
  metaDescription: yup
    .string()
    .trim()
    .max(160, 'SEO description must be 160 characters or fewer')
    .optional(),
  canonicalUrl: optionalUrl('Canonical URL'),
  socialImageUrl: optionalUrl('Social image URL'),
  visibility: yup.string().optional(), // ignored: kept so the untouched visibility <select> doesn't fail validation
}

export const draftSchema = yup.object({
  ...baseFields,
  content: yup
    .mixed<JSONContent>()
    .optional()
    .test('convertible', 'Content cannot be saved', function (value) {
      const result = contentIsConvertible(value)
      return result === true ? true : this.createError({ message: result.message })
    }),
  coverImage: yup.mixed().nullable().optional(),
})

export const publishSchema = yup.object({
  ...baseFields,
  title: yup
    .string()
    .trim()
    .required('Title is required')
    .min(5, 'Title must be at least 5 characters')
    .max(120, 'Title is too long'),
  excerpt: yup
    .string()
    .trim()
    .required('Excerpt is required')
    .min(20, 'Excerpt should be at least 20 characters')
    .max(300, 'Excerpt must be 300 characters or fewer'),
  category: yup.string().trim().required('Category is required').max(80),
  tags: yup
    .array()
    .of(yup.string().trim().max(50, 'Each tag must be 50 characters or fewer').required())
    .min(1, 'Add at least one tag')
    .max(20, 'Use at most 20 tags')
    .required('Tags are required'),
  content: yup
    .mixed<JSONContent>()
    .required('Content is required')
    .test('not-empty', 'Please write some content', (value) => {
      const converted = blocksOf(value)
      return Boolean(converted && converted.blocks.length > 0)
    })
    .test('convertible', 'Content cannot be saved', function (value) {
      const result = contentIsConvertible(value)
      return result === true ? true : this.createError({ message: result.message })
    }),
  coverImage: yup.mixed().nullable().required('Cover image is required'),
  coverImageAltText: yup
    .string()
    .trim()
    .max(300, 'Alt text must be 300 characters or fewer')
    // Backend rule: publishing with a cover image requires alt text.
    .when('coverImage', {
      is: (cover: unknown) => Boolean(cover),
      then: (schema) => schema.required('Add alt text for the cover image'),
    }),
})

export type BlogValidationMode = 'draft' | 'publish'

/**
 * React Hook Form resolver that picks the schema at validation time.
 * `getMode` is read on every validation so the same form can be checked as a
 * draft or for publishing without being rebuilt.
 */
export const createBlogResolver =
  (getMode: () => BlogValidationMode): Resolver<BlogFormValues> =>
  (values, context, options) => {
    const schema = getMode() === 'publish' ? publishSchema : draftSchema
    // The yup schemas describe the same fields as BlogFormValues; the cast only
    // bridges yup's inferred optionality to our hand-written interface.
    const resolver = yupResolver(schema) as unknown as Resolver<BlogFormValues>
    return resolver(values, context, options)
  }
